<template>
  <div class="d-flex align-center cell-container" :class="editingModeClass">
    <div v-if="!editingMode" class="displayed-value">
      {{ displayedValue }}
    </div>
    <div v-if="isSelected" class="d-flex w-100">
      <div v-for="(item, index) in limits" :key="index" class="d-flex w-100">
        <AssortmentTextField
          :ref="
            el => {
              if (el) textFieldRefs[index] = el;
            }
          "
          :key="index"
          v-model="item.value"
          variant="filled"
          bg-color="white"
          width="70"
          :rules="getRules(index)"
          class="text-field"
          @valid="handleErrors(true, index)"
          @invalid="handleErrors(false, index)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { useInputValidations } from '@/composables/input-validations';
import { useAssortmentCanvasStore } from '@/stores/assortment-canvas';
import assortmentCanvases from '@enums/assortment-canvases';
import { isEqual, isNaN } from 'lodash';
import { mapActions, mapState } from 'pinia';

export default {
  name: 'CellValueInputsRenderer',

  setup() {
    const {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    } = useInputValidations();

    return {
      required,
      isInteger,
      isNumber,
      isLessThan,
      isAlphanumeric,
      isPositive,
      isNotEmpty,
      isGreaterThan,
      isGreaterOrEqual,
      isNameUnique,
    };
  },

  data() {
    return {
      limits: null,
      rules: [this.required, v => this.isGreaterOrEqual(v, 0)],
      textFieldRefs: [],
    };
  },
  computed: {
    ...mapState(useAssortmentCanvasStore, ['constraintEditing']),

    editingMode() {
      return (
        this.constraintEditing.constraint.open &&
        isEqual(this.params.node.id, this.constraintEditing.constraint.rowNodeId)
      );
    },

    displayedValue() {
      if (this.params.data.parameter === assortmentCanvases.Constraints.EqualTo) {
        return this.params.value[0];
      }
      if (this.params.value[0] !== null && this.params.value[1] !== null) {
        return `${this.params.value[0]} - ${this.params.value[1]}`;
      }
      if (this.params.value[0] !== null) {
        return this.params.value[0];
      }
      return this.params.value[1];
    },

    selectedParameter() {
      return this.constraintEditing.constraint.rowNodeData.parameter;
    },

    isSelected() {
      return this.editingMode && this.params.node.isSelected();
    },

    isGreaterThanFirstInput() {
      // AOV3-1423 TODO: handle localized numbers, add integer validation if always expecting integer
      // dynamically set the second argument of isGreaterThan to the value of the first input
      let firstInput = parseInt(this.limits[0].value || 0, 10);
      if (firstInput < 0 || isNaN(firstInput)) firstInput = 0;
      const secondInput = parseInt(this.limits[1].value || 1, 10);
      if (secondInput < 0 || isNaN(secondInput)) return () => this.isGreaterOrEqual(secondInput, 0);
      return () => this.isGreaterThan(secondInput, firstInput);
    },

    isLessThanSecondInput() {
      // dynamically set the second argument of isLessThan to the value of the second input
      const firstInput = parseInt(this.limits[0].value || 0, 10);
      if (firstInput < 0 || isNaN(firstInput)) return () => this.isGreaterOrEqual(firstInput, 0);
      let secondInput = parseInt(this.limits[1].value || 1, 10);
      if (secondInput < 0 || isNaN(secondInput)) secondInput = 1;
      return () => this.isLessThan(firstInput, secondInput);
    },

    isSameInput() {
      const firstInput = parseInt(this.limits[0].value || 0, 10);
      const secondInput = parseInt(this.limits[1].value || 0, 10);
      return firstInput === secondInput;
    },

    editingModeClass() {
      return this.editingMode && this.isSelected ? 'editing-padding-top' : '';
    },
  },
  watch: {
    limits: {
      handler() {
        this.$nextTick(() => {
          if (this.isSelected) {
            this.updateData();
            this.revalidateAllFields();
          }
        });
      },
      deep: true,
    },
    selectedParameter() {
      this.init();
    },
    editingMode() {
      this.init();
    },
  },
  mounted() {
    this.init();
  },
  methods: {
    ...mapActions(useAssortmentCanvasStore, [
      'setConstraintRowNodeData',
      'updateConstraintRowNodeData',
      'setConstraintsValidationOccurred',
    ]),
    init() {
      let filteredResult;

      if (this.selectedParameter === assortmentCanvases.Constraints.AtLeast) {
        filteredResult = this.params.value.slice(0, 1).map(x => {
          const value = x === null ? '' : x;
          return { value };
        });
      } else if (this.selectedParameter === assortmentCanvases.Constraints.EqualTo) {
        filteredResult = this.params.value.slice(0, 1).map(x => {
          const value = x === null ? '' : x;
          return { value };
        });
      } else if (this.selectedParameter === assortmentCanvases.Constraints.Between) {
        filteredResult = this.params.value.map(x => {
          const value = x === null ? 1 : x;
          return { value };
        });
      } else if (this.selectedParameter === assortmentCanvases.Constraints.AtMost) {
        filteredResult = this.params.value.slice(1, 2).map(x => {
          const value = x === null ? '' : x;
          return { value };
        });
      }
      this.limits = filteredResult;
    },
    updateData() {
      let newData;
      if (this.selectedParameter === assortmentCanvases.Constraints.AtLeast) {
        newData = [parseInt(this.limits[0].value, 10), null];
      } else if (this.selectedParameter === assortmentCanvases.Constraints.EqualTo) {
        const val = parseInt(this.limits[0].value, 10);
        newData = [val, val];
      } else if (this.selectedParameter === assortmentCanvases.Constraints.Between) {
        newData = [parseInt(this.limits[0].value, 10), parseInt(this.limits[1].value, 10)];
      } else if (this.selectedParameter === assortmentCanvases.Constraints.AtMost) {
        newData = [null, parseInt(this.limits[0].value, 10)];
      }
      this.updateConstraintRowNodeData({ data: newData, field: this.params.field });
    },
    getRules(index) {
      if (this.selectedParameter === assortmentCanvases.Constraints.Between && this.isSameInput) {
        return this.rules;
      }
      if (this.selectedParameter === assortmentCanvases.Constraints.Between && index === 0) {
        return [...this.rules, this.isLessThanSecondInput];
      }
      if (this.selectedParameter === assortmentCanvases.Constraints.Between && index === 1) {
        return [...this.rules, this.isGreaterThanFirstInput];
      }
      return this.rules;
    },
    handleErrors(valuesAreValid, index) {
      // when values are all true, the validation passes. If there is one false, the validation fails.
      this.setConstraintsValidationOccurred({
        // there can be 2 text fields, hence the property name including the index
        [`textFieldValid${index}`]: valuesAreValid,
      });
    },

    revalidateAllFields() {
      // when the limits change, we want to revalidate all fields
      // to make sure the dynamic rules are applied
      this.textFieldRefs.forEach(textField => {
        if (textField) {
          textField.updateAndValidate(
            typeof textField.inputValue === 'string'
              ? textField.inputValue.trim()
              : textField.inputValue
          );
        }
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.cell-container {
  width: 100%;
}

.displayed-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  width: 100%;
}

.v-btn {
  padding-bottom: 0 !important;
}
</style>
